import React, { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Wallet, Coins, Rocket, ExternalLink } from "lucide-react";
import { PROJECT, ASSETS } from "../lib/constants";

const STEPS = [
    {
        icon: Wallet,
        title: "Get a Solana wallet",
        body: "Download Phantom or any Solana-compatible wallet. Fund it with some SOL.",
    },
    {
        icon: Coins,
        title: "Head to Pump.fun",
        body: "Connect your wallet on pump.fun and find $LBRON using the contract address (drops on launch day).",
    },
    {
        icon: Rocket,
        title: "Ape in & hold",
        body: "Swap your SOL for $LBRON, hold the cup, and join the Telegram for everything else.",
    },
];

export const HowToBuy = () => {
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(PROJECT.contractAddress);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            // ignore
        }
    };

    return (
        <section
            id="how-to-buy"
            data-testid="how-to-buy-section"
            className="py-24 sm:py-32 relative overflow-hidden"
        >
            <div className="absolute top-1/2 -left-32 w-[400px] h-[400px] rounded-full blur-[140px] opacity-20 bg-pink-brand pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="mb-16 max-w-3xl">
                    <div className="font-mono-soft text-xs uppercase tracking-[0.3em] text-yellow-brand mb-4">
                        // How to Buy
                    </div>
                    <h2 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.9]">
                        Hold <span className="text-pink-brand">the cup.</span> <br />
                        Hold <span className="text-yellow-brand">the bag.</span>
                    </h2>
                    <p className="mt-5 text-lg text-white/70">
                        $LBRON launches on <span className="text-pink-brand font-bold">{PROJECT.platform}</span> on May 20. Three simple steps.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7 space-y-4">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1 }}
                                    data-testid={`buy-step-${i + 1}`}
                                    className="flex gap-5 p-5 sm:p-6 border-2 border-white/10 hover:border-pink-brand transition-colors bg-[#14110f]"
                                >
                                    <div className="flex-shrink-0">
                                        <div className="w-14 h-14 bg-pink-brand flex items-center justify-center">
                                            <Icon className="w-7 h-7 text-white" />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-mono-soft text-xs uppercase tracking-widest text-yellow-brand mb-1">
                                            Step 0{i + 1}
                                        </div>
                                        <h3 className="font-display text-2xl sm:text-3xl uppercase mb-2">{s.title}</h3>
                                        <p className="text-white/70 text-base">{s.body}</p>
                                    </div>
                                </motion.div>
                            );
                        })}

                        {/* CA box */}
                        <div
                            data-testid="ca-box"
                            className="mt-6 border-2 border-yellow-brand bg-[#0a0806] p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                        >
                            <div>
                                <div className="font-mono-soft text-xs uppercase tracking-widest text-pink-brand mb-1">
                                    Contract Address
                                </div>
                                <div className="font-mono-soft text-sm sm:text-base text-yellow-brand break-all">
                                    {PROJECT.contractAddress}
                                </div>
                            </div>
                            <button
                                data-testid="copy-ca-btn"
                                onClick={copy}
                                className="flex-shrink-0 inline-flex items-center gap-2 bg-pink-brand text-white px-4 py-2.5 font-bold uppercase text-sm tracking-wider hover:bg-yellow-brand hover:text-[#0a0806] transition-colors"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                {copied ? "Copied" : "Copy"}
                            </button>
                        </div>

                        <a
                            href="https://pump.fun"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="pumpfun-link"
                            className="mt-4 inline-flex items-center gap-2 text-yellow-brand hover:text-pink-brand transition-colors font-mono-soft text-sm uppercase tracking-widest"
                        >
                            Visit Pump.fun <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                            className="relative"
                        >
                            <div className="absolute -inset-4 bg-yellow-brand opacity-25 blur-3xl pointer-events-none" />
                            <div className="relative border-4 border-pink-brand brutal-shadow-yellow overflow-hidden">
                                <img
                                    src={ASSETS.coinChart}
                                    alt="$LBRON coin chart"
                                    data-testid="coin-chart-img"
                                    className="w-full block"
                                />
                            </div>
                            <div className="absolute -top-3 -right-3 bg-pink-brand text-white px-4 py-2 font-display text-xl uppercase rotate-3">
                                $LBRON ↗
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
